import { useEffect, useRef } from 'react';
import { FiX } from 'react-icons/fi';
import type { FilterDef, FilterValues } from './filterTypes';

interface FilterPanelProps {
    open: boolean;
    defs: FilterDef[];
    values: FilterValues;
    onChange: (id: string, value: string | boolean) => void;
    onClearAll: () => void;
    onClose: () => void;
}

// Seta customizada única (evita a seta dupla do select nativo com tema; mesmo asset usado
// nos selects do módulo).
const SELECT_CLASS =
    "appearance-none w-full text-sm rounded-md border border-gray-300 dark:border-slate-600 " +
    "bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 pl-2 pr-8 py-2 " +
    "bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke%3D%22%236b7280%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%222%22%20d%3D%22M19%209l-7%207-7-7%22/%3E%3C/svg%3E')] " +
    "bg-[length:1rem] bg-[right_0.5rem_center] bg-no-repeat " +
    "focus:outline-none focus:border-blue-600 dark:focus:border-blue-400";

// Controle "select": opção única, com uma opção vazia ("qualquer"). Fora do componente
// (diretriz: sem função dentro de função).
const SelectControl = ({
    def,
    value,
    onChange,
}: {
    def: FilterDef;
    value: string;
    onChange: (value: string) => void;
}) => (
    <select
        aria-label={def.label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={SELECT_CLASS}
    >
        <option value="">{def.emptyLabel || 'Qualquer'}</option>
        {def.options?.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
        ))}
    </select>
);

// Controle "choice": botões mutuamente exclusivos (ex.: horizontes de prazo). Clicar no
// selecionado desmarca.
const ChoiceControl = ({
    def,
    value,
    onChange,
}: {
    def: FilterDef;
    value: string;
    onChange: (value: string) => void;
}) => (
    <div className="flex gap-1 flex-wrap">
        {def.options?.map((o) => (
            <button
                key={o.value}
                type="button"
                onClick={() => onChange(value === o.value ? '' : o.value)}
                className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${value === o.value
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 border border-gray-200 dark:border-slate-700'}`}
            >
                {o.label}
            </button>
        ))}
    </div>
);

// Controle "toggle": liga/desliga.
const ToggleControl = ({
    def,
    value,
    onChange,
}: {
    def: FilterDef;
    value: boolean;
    onChange: (value: boolean) => void;
}) => (
    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
        <input
            type="checkbox"
            checked={value}
            onChange={(e) => onChange(e.target.checked)}
            className="rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
        />
        <span>{def.label}</span>
    </label>
);

// Uma linha do drawer: rótulo do grupo + o controle apropriado ao tipo do filtro. Toggle
// já traz o próprio rótulo, então não repete o título.
const FilterRow = ({
    def,
    values,
    onChange,
}: {
    def: FilterDef;
    values: FilterValues;
    onChange: (id: string, value: string | boolean) => void;
}) => {
    if (def.kind === 'toggle') {
        return (
            <div className="space-y-1">
                <ToggleControl def={def} value={values[def.id] === true} onChange={(v) => onChange(def.id, v)} />
            </div>
        );
    }
    const stringValue = typeof values[def.id] === 'string' ? (values[def.id] as string) : '';
    return (
        <div className="space-y-1.5">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{def.label}</div>
            {def.kind === 'select' && (
                <SelectControl def={def} value={stringValue} onChange={(v) => onChange(def.id, v)} />
            )}
            {def.kind === 'choice' && (
                <ChoiceControl def={def} value={stringValue} onChange={(v) => onChange(def.id, v)} />
            )}
        </div>
    );
};

// Drawer lateral direito de filtros sob demanda, data-driven. Renderiza um controle por
// FilterDef; aplicar é ao vivo (cada mudança já reflete na grid). Fecha no Escape, no clique
// no backdrop e no botão. Padrão de overlay/foco do SidePanelDrawer. Darkmode.
const FilterPanel = ({ open, defs, values, onChange, onClearAll, onClose }: FilterPanelProps) => {
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    // `onClose` chega inline do pai (recriado a cada render); guardamos a versão mais recente
    // num ref para o listener de Escape não precisar entrar nas deps do efeito. Sem isso, cada
    // mudança de filtro re-rodaria o efeito e roubaria o foco de volta para o botão Fechar,
    // quebrando a navegação por teclado.
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    // Foco inicial + listener de Escape rodam SÓ quando `open` muda (não a cada render). O
    // Escape lê o callback atual do ref.
    useEffect(() => {
        if (!open) return;
        closeButtonRef.current?.focus();
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onCloseRef.current();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open]);

    if (!open) return null;

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-40 z-40" onClick={onClose} />
            <div
                role="dialog"
                aria-modal="true"
                aria-label="Filtros"
                className="fixed right-0 top-0 h-full w-full max-w-sm bg-white dark:bg-slate-800 border-l border-gray-200 dark:border-slate-700 shadow-xl z-50 flex flex-col"
            >
                <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700">
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white">Filtros</h2>
                    <button
                        type="button"
                        ref={closeButtonRef}
                        onClick={onClose}
                        aria-label="Fechar filtros"
                        className="p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
                    >
                        <FiX size={18} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-5">
                    {defs.map((def) => (
                        <FilterRow key={def.id} def={def} values={values} onChange={onChange} />
                    ))}
                </div>

                <div className="flex-shrink-0 flex items-center justify-between gap-2 p-4 border-t border-gray-200 dark:border-slate-700">
                    <button
                        type="button"
                        onClick={onClearAll}
                        className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
                    >
                        Limpar tudo
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                    >
                        Ver resultados
                    </button>
                </div>
            </div>
        </>
    );
};

export default FilterPanel;
